import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../../../../services/api';
import { formatCurrency } from '../../utils/datetime';
import { useWorkspace } from '../../WorkspaceContext';
import { useWorkspaceSales } from '../../hooks/useWorkspaceSales';
import { WORKSPACE_ROOT } from '../../navigation';

const WALLET_CODES = new Set(['MTN', 'VODAFONE', 'AIRTELTIGO', 'MPS']);
const TRANSACTIONS_PATH = `${WORKSPACE_ROOT}/sales/transactions`;

const roundMoney = (value) => Math.round((Number(value) || 0) * 100) / 100;

const digitsOnly = (value) => String(value || '').replace(/\D/g, '');

const formatWalletNumber = (countryCode, phone) => {
  const digits = digitsOnly(phone);
  if (!digits) return '';
  if (countryCode === 'GH') {
    if (digits.startsWith('233')) return digits;
    if (digits.startsWith('0')) return `233${digits.slice(1)}`;
    return `233${digits}`;
  }
  if (countryCode === 'KE') {
    if (digits.startsWith('254')) return digits;
    if (digits.startsWith('0')) return `254${digits.slice(1)}`;
    return `254${digits}`;
  }
  return digits;
};

const isWalletPayout = (payout) => {
  const method = payout?.payoutMethod;
  if (method === 'mobile_money' || method === 'mpesa') return true;
  if (method === 'bank') return false;
  return WALLET_CODES.has(String(payout?.payoutAccountBank || '').toUpperCase());
};

const payoutNumbersMatch = (typed, stored, payout) => {
  if (!typed || !stored) return false;
  const country = String(payout?.payoutCountry || '').toUpperCase();
  if (isWalletPayout(payout) && (country === 'GH' || country === 'KE')) {
    const a = formatWalletNumber(country, typed);
    const b = formatWalletNumber(country, stored);
    return Boolean(a && b && a === b);
  }
  const a = digitsOnly(typed);
  const b = digitsOnly(stored);
  return Boolean(a && b && a === b);
};

const payoutMethodOf = (payout) => payout?.payoutMethod || payout?.method || null;

const isBankDestination = (payout) => {
  const method = payoutMethodOf(payout);
  const bank = payout?.payoutAccountBank;
  if (method === 'bank') return true;
  if (method === 'mobile_money' || method === 'mpesa') return false;
  return Boolean(bank && !WALLET_CODES.has(bank));
};

const destinationKind = (payout) => {
  if (isBankDestination(payout)) return 'bank account number';
  if (payoutMethodOf(payout) === 'mpesa' || payout?.payoutCountry === 'KE') return 'M-Pesa number';
  return 'mobile money number';
};

const numberFieldLabel = (payout) => {
  if (isBankDestination(payout)) return 'Bank account number';
  if (payout?.payoutCountry === 'KE') return 'M-Pesa number';
  return 'Mobile money number';
};

const SalesCashOutConfirm = () => {
  const navigate = useNavigate();
  const locationState = useLocation().state || {};
  const [searchParams] = useSearchParams();
  const { activeLocation } = useWorkspace();
  const { data, loading, error, retrying, cashOut } = useWorkspaceSales();
  const [payoutInfo, setPayoutInfo] = useState(null);
  const [typedNumber, setTypedNumber] = useState('');
  const [touched, setTouched] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const requested = roundMoney(locationState.amount ?? searchParams.get('amount'));
  const currency = data?.currency || 'GHS';
  const max = roundMoney(
    data?.payout?.cashOutMax ?? (data?.payout?.cleared || 0) + (data?.payout?.failed || 0)
  );
  const amount = requested > 0 ? roundMoney(Math.min(requested, max || requested)) : 0;
  const destination = data?.payout?.destination;
  const kind = destinationKind(payoutInfo || destination);
  const feePercent = data?.payout?.platformFeePercent || 0;
  const reservedFee = roundMoney(data?.payout?.platformFeesReserved || 0);

  useEffect(() => {
    if (!activeLocation?.id) return;
    let cancelled = false;
    (async () => {
      const result = await api.getWorkspacePayout(activeLocation.id);
      if (cancelled || result.error) return;
      setPayoutInfo(result.data);
    })();
    return () => {
      cancelled = true;
    };
  }, [activeLocation?.id]);

  const storedNumber = payoutInfo?.payoutAccountNumber || '';
  const matches = useMemo(
    () => payoutNumbersMatch(typedNumber, storedNumber, payoutInfo),
    [typedNumber, storedNumber, payoutInfo]
  );

  const mismatch = !typedNumber.trim()
    ? `Type the ${kind} to confirm where this money is sent.`
    : storedNumber && !matches
      ? `That doesn’t match ${destination?.numberMasked || 'the payout account'} on file.`
      : '';

  if (!requested) {
    return <Navigate to={TRANSACTIONS_PATH} replace />;
  }

  const submit = async (event) => {
    event.preventDefault();
    setTouched(true);
    setSubmitError('');

    if (mismatch) return;

    const result = await cashOut(amount, typedNumber.trim());
    if (result?.error) {
      setSubmitError(result.error);
      return;
    }
    navigate(TRANSACTIONS_PATH, {
      replace: true,
      state: { cashOutSuccess: true, amount: result?.data?.amount || amount },
    });
  };

  return (
    <div className="mx-auto max-w-xl space-y-6">
      <div>
        <Link
          to={TRANSACTIONS_PATH}
          className="text-sm font-medium text-ink-700/80 hover:text-ink-900"
        >
          ← Back to transactions
        </Link>
        <h2 className="mt-3 font-display text-2xl font-light text-ink-900">Confirm cash out</h2>
        <p className="mt-1 text-sm text-ink-700/80">
          Re-enter the {kind} saved in Manage → Payments so this isn’t sent to the wrong account.
        </p>
      </div>

      {submitError && (
        <div className="border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800">
          {submitError}
        </div>
      )}
      {error && !submitError && (
        <div className="border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div>
      )}

      <div className="bg-ink-900 px-5 py-4 text-sand-50">
        <p className="text-[11px] uppercase tracking-[0.14em] text-saffron-400">You receive</p>
        <p className="mt-1 font-display text-3xl font-light tabular-nums">
          {formatCurrency(amount, currency)}
        </p>
        {feePercent > 0 && (
          <p className="mt-2 text-xs leading-snug text-sand-50/55">
            {amount === max && reservedFee > 0
              ? `After fees. Spirara’s ${feePercent}% (${formatCurrency(reservedFee, currency)}) stays in the Spirara wallet and is not sent.`
              : `This is after Flutterwave processing and Spirara’s ${feePercent}% platform fee, which stays in the Spirara wallet.`}
          </p>
        )}
        {max > 0 && amount < requested && (
          <p className="mt-1 text-xs text-sand-50/55">
            Capped at the cleared max of {formatCurrency(max, currency)}.
          </p>
        )}
      </div>

      <div className="bg-sand-50 px-5 py-5 ring-1 ring-ink-900/10">
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-600/60">
          Pays out to
        </p>
        {loading && !destination ? (
          <p className="mt-2 text-sm text-ink-700/70">Loading account…</p>
        ) : destination?.numberMasked ? (
          <>
            <p className="mt-1 font-medium text-ink-900">
              {destination.name || payoutInfo?.payoutAccountName || 'Connected account'}
            </p>
            <p className="mt-0.5 text-sm text-ink-700/80">
              {destination.bank ? `${destination.bank} · ` : ''}
              {destination.numberMasked}
            </p>
          </>
        ) : (
          <p className="mt-2 text-sm text-ink-700/80">
            No payout account is connected.{' '}
            <Link to={`${WORKSPACE_ROOT}/manage/payments`} className="font-medium text-ink-900 underline">
              Set payout details
            </Link>
          </p>
        )}
      </div>

      <form onSubmit={submit} className="space-y-4">
        <div>
          <label htmlFor="confirm-payout-number" className="block text-sm font-medium text-ink-900">
            {numberFieldLabel(payoutInfo || destination)}
          </label>
          <input
            id="confirm-payout-number"
            type="text"
            inputMode="numeric"
            autoComplete="off"
            value={typedNumber}
            onChange={(event) => {
              setTypedNumber(event.target.value);
              setSubmitError('');
            }}
            onBlur={() => setTouched(true)}
            placeholder="Type the full number"
            className="mt-2 w-full bg-white px-3 py-3 text-base text-ink-900 ring-1 ring-inset ring-ink-900/15 focus:outline-none focus:ring-2 focus:ring-saffron-500"
          />
          {touched && mismatch ? (
            <p className="mt-2 text-sm text-red-700">{mismatch}</p>
          ) : matches ? (
            <p className="mt-2 text-sm text-emerald-800">Matches the account on file.</p>
          ) : (
            <p className="mt-2 text-sm text-ink-600/70">
              Type it the way it was saved. A mismatch stops the transfer.
            </p>
          )}
        </div>

        <button
          type="submit"
          disabled={retrying || !destination?.numberMasked || amount <= 0}
          className="w-full bg-saffron-500 px-4 py-3.5 text-sm font-semibold uppercase tracking-[0.14em] text-ink-900 hover:bg-saffron-400 disabled:opacity-40"
        >
          {retrying ? 'Sending…' : 'Confirm and send'}
        </button>

        <p className="text-center text-sm text-ink-600/70">
          Wrong account?{' '}
          <Link to={`${WORKSPACE_ROOT}/manage/payments`} className="font-medium text-ink-900 underline">
            Update payout details
          </Link>
        </p>
      </form>
    </div>
  );
};

export default SalesCashOutConfirm;
